import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from '@mui/material';
import {
    Event as EventIcon,
    Schedule as ScheduleIcon,
    People as PeopleIcon,
    Timer as TimerIcon,
    CalendarToday as CalendarIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { getGameImage } from '../../utils/gameImages';
import { resolveMediaUrl } from '../../utils/mediaUrl';

// Helper functions for date formatting
const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
};

const isTomorrow = (date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.getDate() === tomorrow.getDate() &&
           date.getMonth() === tomorrow.getMonth() &&
           date.getFullYear() === tomorrow.getFullYear();
};

const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
    });
};

const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
    });
};

const formatDistanceToNow = (date) => {
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) {
        return `in ${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
        return `in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    } else {
        return `in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }
};

function SessionsHero({ sessionData, currentUserId, onJoinSession, onLeaveSession, onViewAllSessions }) {
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
    const [sessionToLeave, setSessionToLeave] = useState(null);

    const handleLeaveClick = (sessionId) => {
        setSessionToLeave(sessionId);
        setLeaveDialogOpen(true);
    };

    const handleConfirmLeave = () => {
        if (sessionToLeave) {
            onLeaveSession(sessionToLeave);
        }
        setLeaveDialogOpen(false);
        setSessionToLeave(null);
    };

    const handleCancelLeave = () => {
        setLeaveDialogOpen(false);
        setSessionToLeave(null);
    };

    if (!sessionData) {
        return (
            <Paper
                sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: '#241E18',
                    border: 1,
                    borderColor: 'divider',
                    textAlign: 'center'
                }}
            >
                <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                    No Upcoming Sessions
                </Typography>
            </Paper>
        );
    }

    const { 
        next_session, 
        other_upcoming_count = 0, 
        other_upcoming_sessions = [] 
    } = sessionData;

    // If no next session, show empty state
    if (!next_session) {
        return (
            <Paper
                sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: '#241E18',
                    border: 1,
                    borderColor: 'divider',
                    textAlign: 'center'
                }}
            >
                <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                    No Upcoming Sessions
                </Typography>
            </Paper>
        );
    }

    const formatScheduledTime = (dateString) => {
        const date = new Date(dateString);
        
        if (isToday(date)) {
            return `Today at ${formatTime(date)}`;
        } else if (isTomorrow(date)) {
            return `Tomorrow at ${formatTime(date)}`;
        } else {
            return `${formatDate(date)} at ${formatTime(date)}`;
        }
    };

    const formatRelativeTime = (dateString) => {
        const date = new Date(dateString);
        return formatDistanceToNow(date);
    };

    // Check if current user is already a participant
    const isParticipant = next_session.participants?.some(p => {
        // Compare as strings to handle any type differences
        return String(p.user_id) === String(currentUserId);
    });

    console.log('🎯 SessionsHero debug:', {
        currentUserId,
        participants: next_session.participants,
        isParticipant,
        sessionId: next_session._id || next_session.id
    });

    const getImageUrl = (backgroundImage, gameName) => {
        // Priority 1: Try static image based on game name
        if (gameName) {
            const staticImage = getGameImage({ name: gameName });
            if (staticImage && !staticImage.startsWith('http://') && !staticImage.startsWith('https://')) {
                return staticImage; // This is a static frontend image path
            }
            if (staticImage) return staticImage;
        }
        
        // Priority 2: Use provided background image
        if (!backgroundImage) return null;
        return resolveMediaUrl(backgroundImage);
    };

    return (
        <>
            <Box sx={{ mb: 3 }}>
                {/* Next Session - Large Card */}
                <Paper
                sx={{
                    mb: 2,
                    backgroundColor: '#241E18',
                    border: 2,
                    borderColor: 'secondary.main',
                    overflow: 'hidden',
                    position: 'relative',
                    height: 180
                }}
            >
                {/* Background Image */}
                {(next_session.game_background_image || next_session.game_name) && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundImage: `url(${getImageUrl(next_session.game_background_image, next_session.game_name)})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            opacity: 0.2,
                            zIndex: 0
                        }}
                    />
                )}

                <Grid container sx={{ height: '100%', position: 'relative', zIndex: 1 }}>
                    {/* Left side - Session Info */}
                    <Grid item xs={12} md={8} sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <EventIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                            <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1 }}>
                                Next Session
                            </Typography>
                            {next_session.game_name && (
                                <Chip
                                    label={next_session.game_name}
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, flex: 1 }}>
                            {next_session.title}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ScheduleIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                        {formatScheduledTime(next_session.scheduled_time)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatRelativeTime(next_session.scheduled_time)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TimerIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                <Typography variant="body2">{next_session.duration_minutes} min</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PeopleIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                <Typography variant="body2">
                                    {next_session.participant_count || 0}
                                    {next_session.max_participants && ` / ${next_session.max_participants}`}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right side - Action Button */}
                    <Grid item xs={12} md={4} sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            color={isParticipant ? "success" : "primary"}
                            disabled={!isParticipant && (next_session.max_participants && next_session.participant_count >= next_session.max_participants)}
                            onClick={() => {
                                if (isParticipant) {
                                    handleLeaveClick(next_session._id || next_session.id);
                                } else {
                                    onJoinSession(next_session._id || next_session.id);
                                }
                            }}
                            sx={{ fontWeight: 600, height: 56 }}
                        >
                            {isParticipant ? '✓ Registered' : (next_session.max_participants && next_session.participant_count >= next_session.max_participants ? 'Full' : 'Join Session')}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Other Upcoming Sessions - Horizontal Scroll */}
            {other_upcoming_count > 0 && other_upcoming_sessions && other_upcoming_sessions.length > 0 && (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon sx={{ fontSize: 20 }} />
                            {other_upcoming_count} More Upcoming {other_upcoming_count === 1 ? 'Session' : 'Sessions'}
                        </Typography>
                        <Button size="small" onClick={onViewAllSessions}>
                            View All
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            overflowX: 'auto',
                            pb: 1,
                            '&::-webkit-scrollbar': {
                                height: 6
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(0,0,0,0.2)',
                                borderRadius: 3
                            }
                        }}
                    >
                        {other_upcoming_sessions.map((session, index) => {
                            const isSessionParticipant = session.participants?.some(p => String(p.user_id) === String(currentUserId));
                            const isFull = session.max_participants && session.participant_count >= session.max_participants;
                            
                            return (
                                <Paper
                                    key={session._id || session.id || `session-${index}`}
                                    sx={{
                                        minWidth: 280,
                                        maxWidth: 280,
                                        height: 120,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transition: 'transform 0.2s'
                                    }}
                                >
                                    {/* Background Image */}
                                    {(session.game_background_image || session.game_name) && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundImage: `url(${getImageUrl(session.game_background_image, session.game_name)})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                opacity: 0.3
                                            }}
                                        />
                                    )}

                                    {/* Gradient Overlay */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)'
                                        }}
                                    />

                                    {/* Content */}
                                    <Box sx={{ position: 'relative', p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
                                                {session.title}
                                            </Typography>
                                            {session.game_name && (
                                                <Chip
                                                    label={session.game_name}
                                                    size="small"
                                                    sx={{ mb: 0.5, maxWidth: 'fit-content' }}
                                                />
                                            )}
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ScheduleIcon sx={{ fontSize: 14, color: 'white' }} />
                                                <Typography variant="caption" sx={{ color: 'white' }}>
                                                    {formatScheduledTime(session.scheduled_time)}
                                                </Typography>
                                            </Box>
                                            
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color={isSessionParticipant ? "success" : "primary"}
                                                disabled={!isSessionParticipant && isFull}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (isSessionParticipant) {
                                                        handleLeaveClick(session._id || session.id);
                                                    } else {
                                                        onJoinSession(session._id || session.id);
                                                    }
                                                }}
                                                sx={{ 
                                                    minWidth: 'auto',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {isSessionParticipant ? '✓' : (isFull ? 'Full' : 'Join')}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>

        {/* Leave Confirmation Dialog */}
        <Dialog
            open={leaveDialogOpen}
            onClose={handleCancelLeave}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                Leave Session?
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to leave this session? You can always rejoin if there are spots available.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelLeave} color="inherit">
                    Cancel
                </Button>
                <Button onClick={handleConfirmLeave} variant="contained" color="error">
                    Leave Session
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}

export default SessionsHero;

